import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const eze: ChauffeurPage = {
  slug: "eze",
  title: "VTC & private chauffeur in Èze",
  subtitle:
    "Private chauffeur in Èze: medieval hilltop village, Exotic Garden, La Chèvre d'Or and the panoramic corniche roads.",
  keywords: ["vtc èze", "private chauffeur èze", "eze village chauffeur"],
  hero: {
    title: "VTC & private chauffeur in Èze",
    subtitle:
      "The hilltop village on the Middle Corniche, between Nice and Monaco: a chauffeur for the panoramic roads.",
    keywords: ["VTC Èze", "Èze village chauffeur", "Middle Corniche chauffeur"],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Èze",
    badges: [
      "Corniche roads",
      "Èze Village & Èze-sur-Mer",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Èze",
    items: [
      {
        title: "Corniche roads mastered",
        description:
          "A hilltop village reached by the Middle Corniche: bends, gradient and scarce parking all handled by the chauffeur.",
      },
      {
        title: "Èze Village & Èze-sur-Mer",
        description:
          "Two Èzes, 400 m of altitude apart: the medieval village above, the station and beaches below, linked effortlessly.",
      },
      {
        title: "Hilltop restaurants & palaces",
        description:
          "La Chèvre d'Or and its hotel, Château Eza: drop-off as close as possible to the pedestrian lanes, dinner return handled.",
      },
      {
        title: "Between Nice and Monaco",
        description:
          "A natural stop on a Nice ↔ Monaco day: visit the village then continue to the Principality or Cap-Ferrat.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Èze",
    services: [
      {
        title: "Hilltop village visits",
        text: "Drop-off at the foot of the medieval village, waiting during the Exotic Garden visit and pick-up with no parking constraint.",
      },
      {
        title: "Restaurant & hotel transfers",
        text: "La Chèvre d'Or (2 Michelin stars), Château Eza and hilltop tables: dinner there and back in complete peace of mind.",
      },
      {
        title: "Bespoke panoramic routes",
        text: "Lower, Middle and Grande Corniche: a scenic itinerary between Nice, Èze and Monaco to suit the hour and the light.",
      },
      {
        title: "Airport transfers",
        text: "Èze ↔ Nice airport with flight tracking, via the corniche or the A8 depending on traffic.",
      },
      {
        title: "Èze-sur-Mer & station",
        text: "A link between the village and Èze-sur-Mer (station, beaches, start of the Nietzsche path) in a few minutes.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Èze Village",
        description:
          "Medieval hilltop village, pedestrian lanes and the village entrance.",
      },
      {
        name: "Exotic Garden of Èze",
        description:
          "Cactus garden at the top of the village, sweeping sea views.",
      },
      {
        name: "La Chèvre d'Or",
        description:
          "Two-Michelin-star restaurant and hotel in the heart of the village.",
      },
      {
        name: "Fragonard perfumery",
        description: "Perfume factory and boutique just below the village.",
      },
      {
        name: "Èze-sur-Mer",
        description:
          "Station, beaches and start of the Nietzsche path up to the village.",
      },
      {
        name: "Middle Corniche",
        description: "Panoramic road linking Nice, Èze and Monaco.",
      },
    ],
  },
  generic: {
    heading: "Èze, the eagle's nest of the Middle Corniche",
    subheading:
      "A village perched 400 m above the sea, where the road matters as much as the destination.",
    paragraphs: [
      "Èze has to be earned: the medieval village clings to a rocky spur on the Middle Corniche, above Èze-sur-Mer and its beaches. Between bends, gradient and scarce parking near the village, a chauffeur drops you as close as possible to the pedestrian entrance and collects you on request, with no circling for a space.",
      "Èze's position, halfway between Nice and Monaco, makes it the ideal stop on a day along the corniches. Lower, Middle or Grande Corniche: we choose the most panoramic route to suit the hour and the light, with a stop at the Exotic Garden or the Fragonard perfumery.",
      "For lunch or dinner at La Chèvre d'Or or Château Eza, the chauffeur handles both the journey up and the late return along the corniche, with no constraint of driving after the meal.",
    ],
    bullets: [
      "Corniche routing",
      "Drop-off at the village foot",
      "Dinner return",
    ],
  },
  process: {
    heading: "Book a chauffeur in Èze",
    steps: [
      "Share your programme (village, restaurant, Èze-sur-Mer, airport).",
      "We suggest the corniche route best suited to the hour and the traffic.",
      "The chauffeur drops you at the foot of the village or at the restaurant address.",
      "Visit or dinner at your own pace, pick-up on request for the rest of the programme.",
    ],
  },
  faq: [
    {
      q: "Can you drive into the village of Èze?",
      a: "The heart of the medieval village is entirely pedestrian. The chauffeur drops you as close as possible to the village entrance on the Middle Corniche, then collects you at the same spot after the visit.",
    },
    {
      q: "What is the difference between Èze Village and Èze-sur-Mer?",
      a: "Èze Village is the hilltop village on the Middle Corniche; Èze-sur-Mer is at sea level, with the station and beaches. Nearly 400 m of altitude separate them — the chauffeur links the two in a few minutes.",
    },
    {
      q: "Do you offer a panoramic route along the corniches?",
      a: "Yes. Depending on the hour and the light, we take the Lower, Middle or Grande Corniche between Nice, Èze and Monaco, with photo stops possible at the viewpoints.",
    },
    {
      q: "Do you handle the return after dinner at La Chèvre d'Or?",
      a: "Yes, both the journey there and the late return are covered via the corniche. You enjoy dinner with no driving constraint, and the chauffeur collects you at the agreed time.",
    },
  ],
  relatedLinks: [
    {
      label: "Monaco ⇄ Nice Airport, rate and duration",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    {
      label: "Nice Airport ⇄ Saint-Jean-Cap-Ferrat, rate and duration",
      href: "/en/chauffeur/transfers/nice-airport-saint-jean-cap-ferrat",
    },
    { label: "Routes between Riviera towns", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "Your private chauffeur in Èze",
    text: "Hilltop village, Michelin dinner or the corniche road: book your chauffeur for Èze.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Èze — private chauffeur & hilltop village",
    description:
      "Private car service in Èze: medieval village, Exotic Garden, La Chèvre d'Or and the corniche roads between Nice and Monaco. Flight tracking, 24/7.",
    canonical: "/en/chauffeur/eze",
  },
};

export default eze;
